// src/app/services/ahuApi.ts
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './apiConfig';
import {
  IAHUParams,
  IAhu,
  IAhuCertifications,
  IBlankOff,
  ICasing,
  IComponentData,
  IConstructionMethod,
  IDmount,
  IInternallWall,
  IListItem,
  IPlansInternalWall,
  IQueryString,
  ISection,
  ISpecialOptions,
  ISpecialOptionsSettings,
  ITunnel,
  ITunnelFloor,
  IVoltages,
  IAccessory,
  IComponent,
  UpdatePayload,
  IIdValue,
  ISectionAccessory,
  IAccessoryPosition,
  IOrder,
  IIdNameValue,
  ISelectOption,
  ICreateAHU,
  IConstructionClass,
  IVersions,
} from '../models';
import { getPositionType, getQueryString } from '../helpers/index';
export const ahuApi = createApi({
  reducerPath: 'ahuApi',
  baseQuery,
  endpoints: (builder) => ({
    //Queries
    getVersions: builder.query<IVersions, void>({
      query: () => '/ahu/versions/',
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getAhuParams: builder.query<IAHUParams, number>({
      query: (construction_class_id) =>
        `/api/v1.0/cms/1/v1.0.0/ahu/?construction_class_id=${construction_class_id}`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getConstructionClasses: builder.query<IConstructionClass[], void>({
      query: () => `api/v1.0/cms/1/v1.0.0/ahu/construction_class/`,
      transformResponse: (data: IConstructionClass[]) => {
        data.forEach((e) => (e.value = e.id));
        return data;
      },
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getComponents: builder.query<IComponent[], void>({
      query: () => `/api/v1.0/cms/1/v1.0.0/ahu/components/`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getAccessories: builder.query<IAccessory[], void>({
      query: () => `/api/v1.0/cms/1/v1.0.0/ahu/accessories/`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getTunnelIndexes: builder.query<IIdNameValue[], void>({
      query: () => `/api/v1.0/master/v1.0.0/ahu/tunnel_indexes/`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getConstructionMethod: builder.query<IConstructionMethod[], number>({
      query: (construction_class_id) =>
        `/api/v1.0/cms/1/v1.0.0/ahu/framing/ahu_construction_method_settings/?construction_class_id=${construction_class_id}`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
      transformResponse: (data: {
        ahu_construction_method: IConstructionMethod[];
      }) => {
        return data.ahu_construction_method;
      },
    }),
    getVoltages: builder.query<IVoltages[], void>({
      query: () => `/api/v1.0/master/v1.0.0/ahu/voltages/`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getPowerdBy: builder.query<IIdNameValue[], void>({
      query: () => `/api/v1.0/master/v1.0.0/ahu/powered_by/`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getAirStream: builder.query<IIdNameValue[], void>({
      query: () => `/api/v1.0/master/v1.0.0/ahu/air_streams/`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getRoofLayers: builder.query<IListItem[], void>({
      query: () => `/api/v1.0/master/v1.0.0/ahu/roof_layers/`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getRoofShapes: builder.query<IListItem[], void>({
      query: () => `/api/v1.0/master/v1.0.0/ahu/roof_shapes/`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getRoofSeamDirections: builder.query<IListItem[], void>({
      query: () => `/api/v1.0/master/v1.0.0/ahu/roof_seam_directions/`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getSpecialOptionsSettings: builder.query<ISpecialOptionsSettings[], void>({
      query: () =>
        `/api/v1.0/cms/1/v1.0.0/ahu/special_options/settings_ordered/`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getBlankOff: builder.query<IBlankOff[], void>({
      query: () => `/api/v1.0/ahu/casing/liner_metal_sheets/`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getIntWallPlanes: builder.query<IPlansInternalWall[], void>({
      query: () => `/api/v1.0/cms/1/v1.0.0/ahu/section_internal_wall/planes/`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getPricingMethods: builder.query<IPlansInternalWall[], void>({
      query: () => `/api/v1.0/master/v1.0.0/ahu/pricing_methods/`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getAhuById: builder.query<IAhu, string>({
      query: (id) => `/api/v1.0/ahu/units/${id}`,
    }),
    getAhuTunnels: builder.query<ITunnel[], number>({
      query: (id) => `/api/v1.0/ahu/tunnels/?ahu_id=${id}`,
    }),
    getAhuComponents: builder.query<{ [key: string]: IComponentData }, number>({
      query: (ahu_id) => `/api/v1.0/ahu/get_components/?unit_id=${ahu_id}`,
      transformResponse: (data: IComponentData[]) => {
        const obj: any = {};
        data.map((el) => (obj[el.section_id] = el));
        return obj;
      },
    }),
    getTunnelCasing: builder.query<ICasing, number>({
      query: (id) => `/api/v1.0/ahu/casings/${id}`,
    }),
    getTunnelFloor: builder.query<ITunnelFloor, number>({
      query: (floor_id) => `/api/v1.0/ahu/floors/${floor_id}`,
    }),
    getTunnelSections: builder.query<ISection[], number>({
      query: (tunnel_id) => `/api/v1.0/ahu/sections/?tunnel_id=${tunnel_id}`,
    }),
    getAhuCertifications: builder.query<number[], number>({
      query: (ahu_id) => `/api/v1.0/ahu/units/${ahu_id}/certifications/`,
      transformResponse: (data: IAhuCertifications[]) => {
        return data.map((el) => el.id);
      },
    }),
    getRoofSlopeOrinetation: builder.query<IListItem[], IQueryString>({
      query: (obj) =>
        `/api/v1.0/cms/1/v1.0.0/ahu/roof/params/roof_slope_orientation/${getQueryString(
          obj,
        )}`,
      extraOptions: {
        cacheData: true,
      },
    }),
    getRoofSlopeDirection: builder.query<IListItem[], IQueryString>({
      query: (obj) =>
        `/api/v1.0/cms/1/v1.0.0/ahu/roof/params/roof_slope_direction/${getQueryString(
          obj,
        )}`,
      extraOptions: {
        cacheData: true,
      },
    }),
    getSpecialOptions: builder.query<ISpecialOptions[], string | void>({
      query: (ahu_id) =>
        `/api/v1.0/ahu/special_options/${
          ahu_id ? `?ahu_unit_id=${ahu_id}` : ''
        }`,
    }),
    getHorizontalInternalWalls: builder.query<IInternallWall[], number>({
      query: (section_id) =>
        `/api/v1.0/ahu/sections/horizontal_internal_walls/?section_id=${section_id}`,
    }),
    getVerticalInternalWalls: builder.query<IInternallWall[], number>({
      query: (section_id) =>
        `/api/v1.0/ahu/sections/vertical_internal_walls/?section_id=${section_id}`,
    }),
    getDMounts: builder.query<IDmount[], number>({
      query: (section_id) =>
        `/api/v1.0/ahu/sections/dmounts/?section_id=${section_id}`,
    }),
    getAhuRatedVa24: builder.query<IIdValue[], number>({
      query: (ahu_id) => `/api/v1.0/ahu/xfmr_24v_rated_va/${ahu_id}`,
    }),
    getAhuRatedVa120: builder.query<IIdValue[], number>({
      query: (ahu_id) => `/api/v1.0/ahu/xfmr_120v_rated_va/${ahu_id}`,
    }),
    getSmartListData: builder.query<
      any[],
      { api: string; page?: number; queryString: string }
    >({
      query: ({ api, page, queryString }) =>
        `${api}?page=${page || 1}&${queryString}`,
      extraOptions: {
        cacheData: true,
      },
    }),
    getSectionAccessories: builder.query<ISectionAccessory[], number>({
      query: (section_id) =>
        `/api/v1.0/ahu/accessories/?section_id=${section_id}`,
    }),
    getAccessoryPosition: builder.query<IAccessoryPosition, number>({
      query: (accessory_id) => `/api/v1.0/ahu/positions/${accessory_id}`,
    }),
    getAhuSupplyed: builder.query<IOrder[], void>({
      query: () => `/api/v1.0/master/v1.0.0/ahu/supplyed_by/`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    deleteAccessory: builder.query<void, number>({
      query: (id) => ({
        url: `/api/v1.0/ahu/accessories/${id}`,
        method: 'DELETE',
      }),
    }),
    loadParams: builder.query<
      any[],
      {
        parameter: string;
        queryString: string;
        options: ISelectOption[];
        api: string;
        field_name: string;
      }
    >({
      query: ({ parameter, queryString, api }) => ({
        url: `${
          api ? api : `/api/v1.0/cms/1/v1.0.0/ahu/fans/params/${parameter}`
        }/${queryString}`,
      }),
      extraOptions: {
        cacheData: true,
      },
      transformResponse: (data: any[], _, params) => {
        const new_data: any[] = [];
        data.forEach((el) => {
          if (!el) return;
          if (!params.options) {
            if (params.field_name) {
              el[params.field_name] &&
                new_data.push(el[params.field_name] || el.id || el);
            } else new_data.push(el.id || el);
            return;
          }
          new_data.push(
            params.options.find((item) =>
              typeof el === 'number' ? item.id === el : item.id === el.id,
            ),
          );
        });
        return new_data;
      },
    }),
    //Mutations
    updateAhu: builder.mutation<void, UpdatePayload<IAhu>>({
      query: ({ id, body, signal }) => ({
        url: `/api/v1.0/ahu/units/${id}`,
        method: 'PATCH',
        body,
        signal,
      }),
    }),
    createAhuTunnel: builder.mutation<
      ITunnel,
      {
        body: { name: string; ahu_id: number; index_id: number };
        signal?: AbortSignal;
      }
    >({
      query: ({ body, signal }) => ({
        url: `/api/v1.0/ahu/tunnels/`,
        method: 'POST',
        body,
        signal,
      }),
    }),
    updateAhuTunnel: builder.mutation<void, UpdatePayload<ITunnel>>({
      query: ({ id, body, signal }) => ({
        url: `/api/v1.0/ahu/tunnels/${id}`,
        method: 'PATCH',
        body,
        signal,
      }),
    }),
    updateTunnelCasing: builder.mutation<void, UpdatePayload<ICasing>>({
      query: ({ id, body, signal }) => ({
        url: `/api/v1.0/ahu/casings/${id}`,
        method: 'PATCH',
        body,
        signal,
      }),
    }),
    updateTunnelFloor: builder.mutation<void, UpdatePayload<ITunnelFloor>>({
      query: ({ id, body, signal }) => ({
        url: `/api/v1.0/ahu/floors/${id}`,
        method: 'PATCH',
        body,
        signal,
      }),
    }),
    updateAhuCertifications: builder.mutation<void, UpdatePayload<number>>({
      query: ({ id, body: certification_id, method }) => ({
        url: `/api/v1.0/ahu/units/${id}/certifications/${certification_id}`,
        method: method,
      }),
    }),
    updateSpecialOption: builder.mutation<void, UpdatePayload<any>>({
      query: ({ id, method, body }) => ({
        url: `/api/v1.0/ahu/special_options/${id ? `${id}` : ''}`,
        method: method,
        body,
      }),
    }),
    updateSection: builder.mutation<void, UpdatePayload<ISection>>({
      query: ({ id, body, signal }) => ({
        url: `/api/v1.0/ahu/sections/${id}`,
        method: 'PATCH',
        body,
        signal,
      }),
    }),
    moveSection: builder.mutation<void, UpdatePayload<ISection>>({
      query: ({ id, body, signal }) => ({
        url: `/api/v1.0/ahu/sections/${id}/move`,
        method: 'POST',
        body,
        signal,
      }),
    }),
    createInternalWall: builder.mutation<void, UpdatePayload<IInternallWall>>({
      query: ({ body, type = 'vertical' }) => ({
        url: `/api/v1.0/ahu/sections/${type}_internal_walls/`,
        method: 'POST',
        body,
      }),
    }),
    updateInternalWall: builder.mutation<void, UpdatePayload<IInternallWall>>({
      query: ({ id, body, signal, type = 'vertical', method = 'PATCH' }) => ({
        url: `/api/v1.0/ahu/sections/${type}_internal_walls/${id}`,
        method,
        body,
        signal,
      }),
    }),
    createDMount: builder.mutation<void, UpdatePayload<IDmount>>({
      query: ({ body }) => ({
        url: `/api/v1.0/ahu/sections/dmounts/`,
        method: 'POST',
        body,
      }),
    }),
    updateDMount: builder.mutation<void, UpdatePayload<IDmount>>({
      query: ({ id, body, signal, method = 'PATCH' }) => ({
        url: `/api/v1.0/ahu/sections/dmounts/${id}`,
        method,
        body,
        signal,
      }),
    }),
    updateAccessoryPosition: builder.mutation<
      void,
      UpdatePayload<IAccessoryPosition>
    >({
      query: ({ id, body, signal }) => {
        const type = getPositionType(body?.mounting_plane_id);
        return {
          url: `/api/v1.0/ahu/positions${type ? `/${type}` : ''}/${id}`,
          method: !type ? 'PATCH' : 'PUT',
          body,
          signal,
        };
      },
    }),
    createAHU: builder.mutation<{ id: number }, UpdatePayload<ICreateAHU>>({
      query: ({ body, signal }) => {
        return {
          url: `/api/v1.0/ahu/units/`,
          method: 'POST',
          body,
          signal,
        };
      },
    }),
    deleteAhu: builder.mutation<void, UpdatePayload<{ id: number }>>({
      query: ({ id, signal }) => ({
        url: `/api/v1.0/ahu/units/${id}`,
        method: 'DELETE',
        signal,
      }),
    }),
    rate: builder.mutation<any, UpdatePayload<any>>({
      query: ({ body, signal }) => {
        return {
          url: `/api/v1.0/rating/`,
          method: 'POST',
          body,
          signal,
        };
      },
    }),
    // Section mutations
    createSection: builder.mutation<ISection, UpdatePayload<Partial<ISection>>>(
      {
        query: ({ body, signal }) => ({
          url: `/api/v1.0/ahu/sections/`,
          method: 'POST',
          body,
          signal,
        }),
      },
    ),
    getSection: builder.query<ISection, number>({
      query: (id) => `/api/v1.0/ahu/sections/${id}`,
    }),
    getTunnel: builder.query<ITunnel, number>({
      query: (id) => `/api/v1.0/ahu/tunnels/${id}`,
    }),
    validateTunnel: builder.query<any, number>({
      query: (id) => `/api/v1.0/ahu/tunnels/${id}/validation`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getHands: builder.query<IIdNameValue[], void>({
      query: () => `/api/v1.0/master/v1.0.0/ahu/hands/`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
  }),
});
export const {
  //Queries
  useGetVersionsQuery,
  useLazyGetAhuParamsQuery,
  useGetConstructionClassesQuery,
  useLazyGetAhuByIdQuery,
  useGetAccessoriesQuery,
  useLazyGetAccessoriesQuery,
  useGetComponentsQuery,
  useLazyGetComponentsQuery,
  useLazyGetTunnelIndexesQuery,
  useLazyGetAhuTunnelsQuery,
  useLazyGetAhuComponentsQuery,
  useLazyGetTunnelCasingQuery,
  useLazyGetTunnelFloorQuery,
  useLazyGetTunnelSectionsQuery,
  useGetConstructionMethodQuery,
  useLazyGetAhuCertificationsQuery,
  useGetVoltagesQuery,
  useGetPowerdByQuery,
  useGetAirStreamQuery,
  useGetPricingMethodsQuery,
  useLazyGetRoofLayersQuery,
  useLazyGetRoofSlopeOrinetationQuery,
  useLazyGetRoofSlopeDirectionQuery,
  useLazyGetRoofShapesQuery,
  useLazyGetRoofSeamDirectionsQuery,
  useLazyGetSpecialOptionsQuery,
  useLazyGetSpecialOptionsSettingsQuery,
  useGetBlankOffQuery,
  useLazyGetDMountsQuery,
  useLazyGetHorizontalInternalWallsQuery,
  useLazyGetVerticalInternalWallsQuery,
  useGetIntWallPlanesQuery,
  useGetAhuRatedVa24Query,
  useGetAhuRatedVa120Query,
  useLazyGetSmartListDataQuery,
  useLazyGetSectionAccessoriesQuery,
  useLazyGetAccessoryPositionQuery,
  useGetAhuSupplyedQuery,
  useLazyLoadParamsQuery,
  useGetHandsQuery,
  //Mutations
  useUpdateAhuMutation,
  useUpdateAhuTunnelMutation,
  useUpdateTunnelCasingMutation,
  useUpdateTunnelFloorMutation,
  useUpdateAhuCertificationsMutation,
  useUpdateSpecialOptionMutation,
  useUpdateSectionMutation,
  useCreateDMountMutation,
  useCreateInternalWallMutation,
  useUpdateDMountMutation,
  useUpdateInternalWallMutation,
  useUpdateAccessoryPositionMutation,
  useLazyDeleteAccessoryQuery,
  useCreateAHUMutation,
  useDeleteAhuMutation,
  useRateMutation,
  useCreateSectionMutation,
  useLazyGetSectionQuery,
  useLazyGetTunnelQuery,
  useLazyValidateTunnelQuery,
  useCreateAhuTunnelMutation,
} = ahuApi;
